import React, {useEffect, useState} from 'react';
import {Box, Chip, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate} from 'react-router-dom';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import SearchComponent from '@components/inputs/search/Search.component';
import {CheckCircleGreen, CheckCircleGrey, GroupAction} from '@icons/index';
import {
  PurchaseStatement,
  purchaseStatementsQuery,
  purchaseStatementsService,
  searchPurchaseStatementsEffect
} from '@store/invoices/purchaseStatements';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import {emitOnce} from '@ngneat/elf';
import NewTableComponent from '@components/table/NewTable.component';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridValueFormatterParams, GridValueGetterParams} from '@mui/x-data-grid-pro';
import {Invoice, InvoiceStatus, InvoiceType} from '@store/invoices/invoices';
import {OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import {NumberExt} from '@utils/extensions/number.extension';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import DateRangePickerComponent from '@components/inputs/dateRangePicker/DateRangePicker.component';
import {useSnackbar} from 'notistack';
import PurchaseStatementGroupedActionModal
  from '@screens/admin/invoices/purchaseStatement/PurchaseStatementGroupedAction.modal';
import SelectComponent from '@components/inputs/select/Select.component';
import IconButtonMenuComponent from '@components/buttons/IconButtonMenu.component';
import {Download} from '@mui/icons-material';
import ExpandableIconButtonComponent from '@components/buttons/ExpandableIconButton.component';

const PurchaseStatementTab = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const searchPurchaseStatements = useEffectFn(searchPurchaseStatementsEffect);
  const {enqueueSnackbar} = useSnackbar();

  const [{purchaseStatements, loading}] = useObservable(purchaseStatementsQuery.purchaseStatements$);
  const [selectedPurchaseStatements] = useObservable(purchaseStatementsQuery.selectedPurchaseStatements$);
  const [openGroupedActionModal, setOpenGroupedActionModal] = useState(false);


  const [filters] = useObservable(purchaseStatementsQuery.filters$);
  const [sortModel] = useObservable(purchaseStatementsQuery.sort$);
  const [tablePagination] = useObservable(purchaseStatementsQuery.pagination$);

  useEffect(() => {
    searchPurchaseStatements({filters, tablePagination, sort: sortModel});
  }, [filters, searchPurchaseStatements, sortModel, tablePagination]);

  const handleExportCSV = () => {
    purchaseStatementsService.getPurchasesCSV().subscribe({
      error: err => enqueueSnackbar(err.text, err.options),
    });
  };

  const handleExportCSVInvoices = () => {
    purchaseStatementsService.getPurchasesPiecesCSV().subscribe({
      error: err => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack flex={1} spacing={1} pb="25px" mt="-35px">
      <Stack alignSelf="flex-end" direction="row" alignItems="center" spacing={2}>
        <ExpandableIconButtonComponent
          variant="outlined" color="primary"
          disabled={!selectedPurchaseStatements.length}
          onClick={() => setOpenGroupedActionModal(true)}
          title={t('global.groupedAction')}
          startIcon={<GroupAction/>}/>
        <IconButtonMenuComponent variant="outlined" icon={<Download/>} color="primary" items={[{
          title: t('global.exportCSVList'),
          onClick: handleExportCSV
        }, {title: t('invoices.bills.exportRACSV'), onClick: handleExportCSVInvoices}]}/>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <Box>
            <DateRangePickerComponent
              size="small"
              inputWidth={140}
              placeholderStart={t('invoices.executions.filters.from')}
              placeholderEnd={t('invoices.executions.filters.to')}
              startDate={filters.from}
              endDate={filters.to}
              handleChangeStartDate={purchaseStatementsService.setFilters('from')}
              handleChangeEndDate={purchaseStatementsService.setFilters('to')}/>
          </Box>
        </Stack>
        <Box>
          <CheckboxComponent
            handleChange={(value) => {
              emitOnce(() => {
                purchaseStatementsService.setFilters('filterAccountantTransferred')(value);
                purchaseStatementsService.setPagination('page')(1);
              });
            }}
            value={filters.filterAccountantTransferred}
            label={t('invoices.purchaseStatement.filters.hideAccountantTransferred')}/>
        </Box>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2}>

          <Box width="500px">
            <SearchComponent
              value={filters.search}
              handleChange={(value) => {
                emitOnce(() => {
                  purchaseStatementsService.setFilters('search')(value);
                  purchaseStatementsService.setPagination('page')(1);
                });
              }}/>
          </Box>
          <Box width="200px">
            <SelectComponent
              size="small"
              inputWidth={140}
              value={filters.status}
              items={InvoiceStatus.selectItems()}
              handleChange={(value) => {
                purchaseStatementsService.setFilters('status')(value);
                purchaseStatementsService.setPagination('page')(1);
              }}
              placeholder={t('invoices.invoicesValidation.filters.status')}
            />
          </Box>
        </Stack>
        <TableFiltersComponent
          filters={filters}
          handleSave={purchaseStatementsService.setFilters('tableFilters')}
          handleReset={() => purchaseStatementsService.resetFilters()}/>
      </Stack>
      <Stack flex={1}>
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.CUSTOM({
              field: 'type',
              label: '',
              renderCell: (params: GridValueGetterParams<any, InvoiceType>) => {
                return <Chip label={t(InvoiceType.getLabel(params?.value || InvoiceType.DEFAULT))}
                             sx={{backgroundColor: InvoiceType.getColor(params?.value || InvoiceType.DEFAULT)}}></Chip>;
              }
            }),
            COLUMN_TYPES.TEXT_BOLD({
              field: 'supplierInvoice.invoiceDate',
              label: t('invoices.purchaseStatement.table.invoiceDate'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, PurchaseStatement>) => params.row.invoiceDate,
              valueFormatter: (params: GridValueFormatterParams) => params.value ? t('global.date', {date: new Date(params.value)}) : '',
              width: 150,
            }),
            COLUMN_TYPES.TEXT_BOLD({
              field: 'supplierInvoice.createdAt',
              label: t('invoices.purchaseStatement.table.generated'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, PurchaseStatement>) => params.row.createdAt,
              valueFormatter: (params: GridValueFormatterParams) => params.value ? t('global.date', {date: new Date(params.value)}) : '',
              width: 110,
            }),
            COLUMN_TYPES.TEXT_BOLD({
              field: 'invoice.externalRef',
              label: t('invoices.purchaseStatement.table.internalRef'),
              valueGetter: (params: GridValueGetterParams<any, PurchaseStatement>) => params.row.invoiceRef,
              width: 160,
            }),
            COLUMN_TYPES.TEXT_BOLD({
              field: 'supplierInvoice.externalRef',
              label: t('invoices.purchaseStatement.table.number'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, PurchaseStatement>) => params.row.invoiceNumber,
              width: 160,
            }),
            COLUMN_TYPES.TEXT({
              field: 'soUnit.name',
              color: 'secondary',
              label: t('invoices.purchaseStatement.table.supplier'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, PurchaseStatement>) => params.row.supplierUnitName,
              fullWidth: true,
              minWidth: 350,
            }),
            COLUMN_TYPES.TEXT({
              field: 'client',
              color: 'secondary',
              label: t('invoices.purchaseStatement.table.client'),
              valueGetter: (params: GridValueGetterParams<any, PurchaseStatement>) =>
                params.row.clientCount > 1 ? t('global.multiple', {count: params.row.clientCount}) : params.row.clientName,
              fullWidth: true,
              minWidth: 350,
            }),
            COLUMN_TYPES.TEXT({
              field: 'site',
              color: 'secondary',
              label: t('invoices.purchaseStatement.table.site'),
              valueGetter: (params: GridValueGetterParams<any, PurchaseStatement>) =>
                params.row.unitCount > 1 ? t('global.multiple', {count: params.row.unitCount}) : params.row.unitName,
              fullWidth: true,
              minWidth: 350,
            }),
            COLUMN_TYPES.TEXT({
              field: 'dueDate',
              color: 'secondary',
              label: t('invoices.purchaseStatement.table.dueDate'),
              valueFormatter: (params: GridValueFormatterParams) => params.value ? t('global.date', {date: new Date(params.value)}) : '',
              width: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'invoice.totalAmountNoVatInCents',
              label: t('invoices.purchaseStatement.table.amountHT'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, PurchaseStatement>) => {
                const price = params.row.totalAmountNoVatInCents;
                return price !== undefined ? `${NumberExt.round(price / 100, 2)} ${params.row.currency}` : '-';
              },
              width: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'invoice.totalAmountVatIncludedInCents',
              label: t('invoices.purchaseStatement.table.amount'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, PurchaseStatement>) => {
                const price = params.row.totalAmountVatIncludedInCents;
                return price !== undefined ? `${NumberExt.round(price / 100, 2)} ${params.row.currency}` : '-';
              },
              width: 150,
            }),
            COLUMN_TYPES.SELECT({
              field: 'invoice.status',
              label: t('global.status'),
              options: InvoiceStatus.selectItems(),
              disabled: () => true,
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, PurchaseStatement>) => params.row.status,
              width: 125,
            }),
            COLUMN_TYPES.ICON({
              field: 'accountingTransferDate',
              label: t('invoices.bills.table.accountingTransferDate'),
              renderIcon: (invoiceLine: Invoice) =>
                invoiceLine.accountingTransferDate ? <CheckCircleGreen/> : <CheckCircleGrey/>,
              minWidth: 140,
            }),
            COLUMN_TYPES.ACTIONS({
              handleShowRow: (invoice: Invoice) => navigate(`${invoice.id}`),
              otherActions: [
                {
                  type: OtherActionType.PDF,
                },
                {
                  type: OtherActionType.SHARE,
                },
              ],
            }),
          ]}
          handleReload={() => searchPurchaseStatements({filters, tablePagination, sort: sortModel})}
          handlePageSizeChange={purchaseStatementsService.setPagination('limit')}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => purchaseStatementsService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          handleSortModelChange={purchaseStatementsService.setSort}
          sortModel={[sortModel]}
          handleRowSelected={purchaseStatementsService.setSelectedPurchaseStatements}
          rowsSelected={selectedPurchaseStatements}
          totalRows={purchaseStatementsQuery.totalRows}
          data={purchaseStatements}/>
      </Stack>
      <Outlet/>
      {openGroupedActionModal && (
        <PurchaseStatementGroupedActionModal handleClose={() => setOpenGroupedActionModal(false)}/>)}
    </Stack>
  );
};

export default PurchaseStatementTab;
